import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { getStateFromPath } from '@react-navigation/native';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { Button } from 'design-system/Button';
import Progress from 'design-system/Progress';
import { PermissionStatus } from 'hooks/usePermissionsCamera';
import { useAppSelector } from 'store/hooks';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppLink from 'hooks/useAppLink';
import { DeepLinkType, parseDeepLink, } from 'common/NavigatorActions/hooks/parseDeepLink';
import { getWebSignInQRCodeStatus } from 'actions';
import { useDefaultAccountPicker } from 'features/pay/hooks/useDefaultAccountPicker';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import Modal from 'utils/packages/Modal';
import { selectUser } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import QRFrame from './QRFrame';
import ScanQRAndroid from './ScanQRAndroid';
import ScanQRiOS from './ScanQRiOS';
const ScanQR = Platform.OS === 'ios' ? ScanQRiOS : ScanQRAndroid;
const ScanQRView = ({ title, visible, flash, status, onRequestPermission, bankAccount, }) => {
    const { deviceWidth: width } = useAppFrameDimensions();
    const webSignInConfirmation = useAppSelector((store) => store.signInWeb.webSignInConfirmation);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const { appLinkToFromUrl } = useAppLink();
    const [timeoutActive, setTimeOutActive] = useState(false);
    const timeoutRef = useRef(undefined);
    const loading = isFetching || !!webSignInConfirmation || timeoutActive;
    const [, picker] = useDefaultAccountPicker();
    const user = useAppSelector(selectUser);
    const navigation = useMainStackNavigation();
    const onConsumeQRCode = useCallback(async (codes) => {
        if (loading || !codes?.[0])
            return;
        setTimeOutActive(true);
        const deepLinkData = parseDeepLink(codes[0]);
        if (deepLinkData.type === DeepLinkType.WEB_SIGN_IN) {
            await fetch(getWebSignInQRCodeStatus({ ...deepLinkData, popOnComplete: true }));
        }
        else if (codes[0].includes('/@')) {
            const from = bankAccount || (await picker());
            const routeState = getStateFromPath(codes[0].split('/@')[1]);
            if (from && routeState) {
                const handle = routeState.routes[0]?.name?.replace('@', '');
                const params = routeState.routes[0]?.params;
                const payeeAccountId = params?.accountId
                    ? Number(params.accountId)
                    : undefined;
                if (user.urlHandle.toLowerCase() === handle) {
                    Modal.showError('You can’t request money from yourself; share this code to get paid in Emma', () => { }, 'Hold on 💫', 'Okay');
                }
                else {
                    navigation.navigateDeprecated('PayEnterAmount', {
                        to: {
                            payeeAccountId,
                            itemType: 'pay',
                            contact: { handle, phoneNumbers: [] },
                        },
                        from,
                    });
                }
            }
        }
        else {
            appLinkToFromUrl(codes[0], false);
        }
        timeoutRef.current = setTimeout(() => {
            setTimeOutActive(false);
        }, 500);
    }, [appLinkToFromUrl, bankAccount, loading, picker, user.urlHandle]);
    useEffect(() => () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);
    const renderInner = () => {
        if (status !== PermissionStatus.allowed) {
            return (<>
          <Text Text-20 White style={styles.permissionTitle}>
            Camera permissions required
          </Text>
          <Button hug brand small title="Continue" style={styles.button} onPress={onRequestPermission}/>
        </>);
        }
        if (loading) {
            return <Progress style={styles.progress}/>;
        }
        return null;
    };
    return (<View style={[styles.content, { width }]}>
      {status === PermissionStatus.allowed ? (<ScanQR visible={visible} flash={flash} onScannedCodes={onConsumeQRCode}/>) : null}
      <View style={styles.permission}>
        <QRFrame style={styles.frame}/>
        {renderInner()}
      </View>
      <Text Text-20 White style={styles.title} centered>
        {title}
      </Text>
    </View>);
};
const styles = StyleSheet.create({
    content: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'black',
    },
    permission: {
        height: rem(226),
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    },
    permissionTitle: {
        marginTop: rem(24),
        marginHorizontal: rem(16),
        fontSize: rem(20),
    },
    button: {
        marginTop: rem(24),
    },
    progress: {
        width: rem(70),
        height: rem(70),
    },
    title: {
        marginHorizontal: rem(16),
        marginTop: rem(52),
        fontSize: rem(20),
    },
    frame: {
        position: 'absolute',
        top: 0,
    },
});
export default ScanQRView;
